export { EmailPanel }

import { useState } from 'react'
import type { FormEvent } from 'react'
import { describeUnexpectedError } from '../../components/describeError.ts'
import { isNotLoggedInAbort } from '../../components/abortValue.ts'
import { clearWhenSettled } from '../../components/clearWhenSettled.ts'
import { onChangeEmail } from './EmailPanel.telefunc.ts'
import { panelStyle as style } from './styles.ts'

type Result =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; email: string }
  | { kind: 'unchanged' }
  | { kind: 'inputErrors'; message: string }
  | { kind: 'error'; message: string }

function EmailPanel({ currentEmail, onUpdate }: { currentEmail: string; onUpdate: (next: string) => void }) {
  const [newEmail, setNewEmail] = useState('')
  const [result, setResult] = useState<Result>({ kind: 'idle' })

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (result.kind === 'submitting') return
    const trimmed = newEmail.trim()
    if (!trimmed) return
    setResult({ kind: 'submitting' })
    try {
      const res = await onChangeEmail(trimmed)
      if ('inputErrors' in res) {
        setResult({ kind: 'inputErrors', message: res.inputErrors.email })
        return
      }
      if (res.noOp) {
        setResult({ kind: 'unchanged' })
      } else {
        onUpdate(res.email)
        setResult({ kind: 'success', email: res.email })
        setNewEmail('')
      }
    } catch (err) {
      // Session expired mid-action: the global `useTelefuncAbort` handler is already reloading
      // to <Login>, and it's an EXPECTED condition — so bow out rather than logging it as a bug
      // (console.error → Sentry) or flashing a generic error the reload would discard anyway.
      if (isNotLoggedInAbort(err)) return
      console.error('onChangeEmail failed:', err)
      setResult({ kind: 'error', message: describeUnexpectedError(err) })
    }
  }

  return (
    <section style={style.panel}>
      <h2 style={style.h2}>Email</h2>
      <p style={style.hint}>
        Current: <strong>{currentEmail}</strong>
      </p>
      <form onSubmit={submit} style={style.form}>
        <label style={style.field}>
          <div style={style.label}>New email address</div>
          <input
            type="email"
            required
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value)
              clearWhenSettled(result, setResult, { kind: 'idle' })
            }}
            placeholder="hi@example.com"
            style={style.input}
          />
        </label>
        <button type="submit" disabled={result.kind === 'submitting'} style={style.button}>
          {result.kind === 'submitting' ? 'Updating…' : 'Change email'}
        </button>
      </form>
      {result.kind === 'success' && (
        <p style={style.success}>
          Email updated to <strong>{result.email}</strong>. You're still signed in — your license keys are unchanged.
          Future sign-in links will go to the new address.
        </p>
      )}
      {result.kind === 'unchanged' && <p style={style.neutral}>That's already your email — no change made.</p>}
      {result.kind === 'inputErrors' && <p style={style.error}>{result.message}</p>}
      {result.kind === 'error' && <p style={style.error}>{result.message}</p>}
    </section>
  )
}
